import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useOrg } from '../hooks/useOrg';
import { api } from '../lib/api';

export const LocationContext = createContext(null);

// Sentinel value used in localStorage and the context to mean "show data
// across every location in the org" rather than scoping to one.
export const ALL_LOCATIONS = '__all__';

const lsKey = (orgId) => `pi-portal-current-location:${orgId}`;

export function LocationProvider({ children }) {
  const { currentOrg } = useOrg();
  const [locations, setLocations] = useState([]);
  const [currentLocationId, setCurrentLocationId] = useState(null);
  const [restricted, setRestricted] = useState(false); // member limited to assigned offices
  const [loading, setLoading] = useState(false);

  const fetchLocations = useCallback(async () => {
    if (!currentOrg?.id) {
      setLocations([]);
      setCurrentLocationId(null);
      return;
    }
    setLoading(true);
    try {
      // The server already returns only the offices this member may see;
      // `scope` says whether that is a restriction (then no "All locations").
      const [data, scope] = await Promise.all([
        api.getLocations(currentOrg.id),
        api.getLocationScope(currentOrg.id).catch(() => ({ restricted: false })),
      ]);
      setLocations(data || []);
      setRestricted(!!scope?.restricted);

      const saved = localStorage.getItem(lsKey(currentOrg.id));
      if (saved === ALL_LOCATIONS && !scope?.restricted) {
        setCurrentLocationId(ALL_LOCATIONS);
      } else if (saved && (data || []).some(l => l.id === saved)) {
        setCurrentLocationId(saved);
      } else {
        const primary = (data || []).find(l => l.is_primary) || (data || [])[0];
        setCurrentLocationId(primary?.id || null);
      }
    } catch (e) {
      console.error('[LocationContext] fetch failed:', e.message);
      setLocations([]);
      setCurrentLocationId(null);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const switchLocation = useCallback((locationId) => {
    setCurrentLocationId(locationId);
    if (currentOrg?.id) {
      localStorage.setItem(lsKey(currentOrg.id), locationId || '');
    }
  }, [currentOrg]);

  const isAllLocations = currentLocationId === ALL_LOCATIONS;
  const currentLocation = isAllLocations
    ? null
    : locations.find(l => l.id === currentLocationId) || null;

  // Caller passes this to the API for filtering: undefined = no filter
  // (used when the dropdown is on "All locations").
  const apiLocationFilter = isAllLocations ? undefined : currentLocationId || undefined;

  const value = {
    locations,
    currentLocation,
    currentLocationId,
    isAllLocations,
    apiLocationFilter,
    restricted,
    loading,
    switchLocation,
    refetchLocations: fetchLocations,
  };

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}
